import { StatePage } from "@/components/state-page";
import { filterKeys } from "@/utils";
import { orpc } from "@/utils/clients/orpc";
import { CONSTANTS } from "@/utils/constants";
import { requirePermissions } from "@/utils/permissions";
import { Button } from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, useSearch } from "@tanstack/react-router";
import { t } from "i18next";
import { LucideDownload, LucidePlus } from "lucide-react";
import RolesControlBar from "./-control-bar";
import RolesList from "./-list";
import NewRole from "./-new";
import { RoleFiltersSchema } from "./-utils";

export const Route = createFileRoute("/_authenticated/roles/")({
  component: Component,
  beforeLoad: requirePermissions(true),
  validateSearch: RoleFiltersSchema,
  loader: ({ context }) => {
    void context.queryClient.prefetchQuery(orpc.role.getAll.queryOptions());
  },
  errorComponent: ({ error, reset }) => (
    <StatePage error={error} reset={reset} resourceType="roles" emptyResourceType="roles" />
  ),
  notFoundComponent: () => <StatePage isNotFound resourceType="roles" emptyResourceType="roles" />,
});

function Component() {
  const search = useSearch({ from: "/_authenticated/roles/" });
  const queryClient = useQueryClient();
  const filters = filterKeys(search, RoleFiltersSchema.shape);

  const exportAllMutation = useMutation({
    mutationFn: async () => {
      const [{ pdf, Document }, { RolePDFPage }, agency, allRoles] = await Promise.all([
        import("@react-pdf/renderer"),
        import("./-pdf-document"),
        queryClient.fetchQuery(orpc.agency.get.queryOptions()),
        queryClient.fetchQuery(orpc.role.getAll.queryOptions()),
      ]);

      // Apply client-side filtering
      const filteredRoles = allRoles.filter((role) => {
        if (filters.search) {
          const searchLower = filters.search.toLowerCase();
          const matchesSearch =
            role.name?.toLowerCase().includes(searchLower) || role.id.toLowerCase().startsWith(searchLower);
          if (!matchesSearch) return false;
        }

        if (filters.permissions && filters.permissions.length > 0) {
          const hasAllPermissions = filters.permissions.every((p) => role.permissions.includes(p));
          if (!hasAllPermissions) return false;
        }

        return true;
      });

      if (filteredRoles.length === 0) {
        throw new Error(t("roles.exportPDF.all.empty"));
      }

      // Prepare PDF pages
      const rolePages = filteredRoles.map((role) => (
        <RolePDFPage key={role.id} role={role} agency={agency} baseUrl={window.location.origin} />
      ));

      const doc = <Document>{rolePages}</Document>;

      const blob = await pdf(doc).toBlob();

      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      const dateStr = new Date().toISOString().split("T")[0];
      link.download = `roles-${dateStr}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    },
    onSuccess: () => {
      notifications.show({
        title: t("success"),
        message: t("roles.exportPDF.all.success.description"),
        color: "green",
      });
    },
    onError: (error) => {
      notifications.show({
        title: t("error.title"),
        message: error instanceof Error ? error.message : t("roles.exportPDF.all.error.description"),
        color: "red",
      });
    },
  });

  return (
    <div className="gap-md md:gap-lg flex grow flex-col">
      <div className="py-md flex flex-wrap items-center justify-between gap-x-6 gap-y-4 md:justify-start">
        <p className="text-2xl font-semibold md:text-3xl">{t("roles.yourRoles")}</p>
        <div className="grid grow grid-cols-2 items-center gap-1.5 md:flex">
          <Button
            variant="default"
            leftSection={<LucideDownload className="size-4" />}
            onClick={() => exportAllMutation.mutate()}
            loading={exportAllMutation.isPending}
            disabled={CONSTANTS.DEMO}
          >
            {t("export")}
          </Button>
          <NewRole
            target={(onClick) => (
              <Button onClick={onClick} leftSection={<LucidePlus />}>
                {t("roles.new")}
              </Button>
            )}
          />
        </div>
      </div>

      <RolesControlBar />
      <RolesList />
    </div>
  );
}
